import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CommentIcon from '@mui/icons-material/Comment';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChapterReaderLogic } from '../hooks/use-chapter-reader-logic';
import { ReaderFeedbackDrawer } from './ReaderFeedbackDrawer';
import { ReaderSettingsMenu } from './ReaderSettingsMenu';
import { ReaderSidebar } from './ReaderSidebar';

export function ChapterReader({ novelId, chapterId }: { novelId: string; chapterId: string }) {
  const { t } = useTranslation();
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentText, setCommentText] = useState('');

  const {
    novel, fontSize, fontFamily, themeMode, setThemeMode, setFontSize, setFontFamily,
    settingsAnchor, setSettingsAnchor, drawerOpen, setDrawerOpen, isLoadingContent,
    currentChapter, previousChapter, nextChapter, comments, editor,
    handleSaveComment, handleSaveReply, handleDeleteComment, canManageAllComments,
  } = useChapterReaderLogic(novelId, chapterId);

  const themeStyles = useMemo(() => {
    if (themeMode === 'sepia') return { bgcolor: '#f4ecd8', color: '#433422', borderColor: '#e6dcc5', paperBg: '#fcf5e3' };
    if (themeMode === 'dark') return { bgcolor: '#121212', color: '#e0e0e0', borderColor: '#2d2d2d', paperBg: '#1e1e1e' };
    return { bgcolor: '#ffffff', color: '#1a1a1a', borderColor: '#e5e7eb', paperBg: '#f9fafb' };
  }, [themeMode]);

  const fontStyles = useMemo(() => {
    const family = fontFamily === 'serif' ? 'Georgia, serif' : 'system-ui, -apple-system, sans-serif';
    const size = fontSize === 'small' ? '0.95rem' : fontSize === 'large' ? '1.3rem' : '1.1rem';
    const lineHeight = fontSize === 'small' ? '1.6' : fontSize === 'large' ? '1.85' : '1.75';
    return { fontFamily: family, fontSize: size, lineHeight };
  }, [fontFamily, fontSize]);

  const quotedText = useMemo(() => {
    if (!editor) return '';
    const { from, to } = editor.state.selection;
    return from === to ? '' : editor.state.doc.textBetween(from, to);
  }, [editor, commentDialogOpen]);

  const handlePostComment = async () => {
    if (!commentText.trim()) return;
    await handleSaveComment(commentText);
    setCommentText('');
    setCommentDialogOpen(false);
  };

  if (isLoadingContent || !novel.id || !currentChapter) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <ReaderSidebar /><Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <ReaderSidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: themeStyles.bgcolor, color: themeStyles.color, transition: 'all 0.3s ease' }}>
        {/* Top Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: themeStyles.borderColor, bgcolor: themeStyles.paperBg }}>
          <Link to="/read/$novelId" params={{ novelId }} style={{ textDecoration: 'none' }}>
            <Button startIcon={<ArrowBackIcon />} sx={{ color: themeStyles.color, textTransform: 'none' }}>{t('common.back')}</Button>
          </Link>
          <Box sx={{ textAlign: 'center', mx: 2, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.8 }} noWrap>{novel.title}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>{currentChapter.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ color: themeStyles.color }}><SettingsIcon /></IconButton>
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: themeStyles.color }}><CommentIcon /></IconButton>
          </Box>
        </Box>
        {/* Text Area */}
        <Box onClick={(e) => {
          const target = e.target as HTMLElement;
          const commentId = target.getAttribute('data-comment-id') || target.closest('.comment-mark')?.getAttribute('data-comment-id') || target.closest('.commented-paragraph')?.getAttribute('data-comment-id');
          if (commentId) setDrawerOpen(true);
        }} sx={{ flexGrow: 1, overflowY: 'auto', p: 4, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{
            width: '100%', maxWidth: 700, py: 4,
            '& .ProseMirror': { fontFamily: fontStyles.fontFamily, fontSize: fontStyles.fontSize, lineHeight: fontStyles.lineHeight, outline: 'none' },
            '& .reader-leading-indent': { float: 'left', fontSize: '1em', lineHeight: fontStyles.lineHeight, whiteSpace: 'pre' },
            '& .reader-drop-cap': { fontSize: '3.5em', float: 'left', lineHeight: 0.8, marginTop: '0.1em', marginRight: '0.1em', fontFamily: 'Georgia, serif', fontWeight: 'bold' },
            '& .comment-mark': { backgroundColor: 'rgba(234, 179, 8, 0.25)', borderBottom: '2.5px solid #eab308', cursor: 'pointer', transition: 'background-color 0.2s', '&:hover': { backgroundColor: 'rgba(234, 179, 8, 0.45)' } },
            '& .commented-paragraph': { borderLeft: '4px solid #eab308', paddingLeft: '16px', backgroundColor: 'rgba(234, 179, 8, 0.04)' }
          }}><EditorContent editor={editor} /></Box>
        </Box>
        {/* Bottom Nav */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 4, py: 2.5, borderTop: '1px solid', borderColor: themeStyles.borderColor, bgcolor: themeStyles.paperBg }}>
          {previousChapter ? (
            <Link to="/read/$novelId/$chapterId" params={{ novelId, chapterId: previousChapter.id }} style={{ textDecoration: 'none' }}>
              <Button startIcon={<ChevronLeftIcon />} sx={{ color: themeStyles.color, textTransform: 'none' }}>{previousChapter.name}</Button>
            </Link>
          ) : <Box />}
          {nextChapter ? (
            <Link to="/read/$novelId/$chapterId" params={{ novelId, chapterId: nextChapter.id }} style={{ textDecoration: 'none' }}>
              <Button endIcon={<ChevronRightIcon />} sx={{ color: themeStyles.color, textTransform: 'none' }}>{nextChapter.name}</Button>
            </Link>
          ) : <Box />}
        </Box>
      </Box>

      {/* Bubble Menu */}
      {editor && (
        <BubbleMenu editor={editor}>
          <Paper elevation={4} sx={{ p: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Button size="small" startIcon={<CommentIcon />} onClick={() => setCommentDialogOpen(true)} variant="contained" sx={{ textTransform: 'none', borderRadius: 1.5 }}>
              {t('reader.bubble.comment')}
            </Button>
          </Paper>
        </BubbleMenu>
      )}

      <ReaderSettingsMenu
        anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)}
        fontSize={fontSize} onFontSizeChange={setFontSize} fontFamily={fontFamily} onFontFamilyChange={setFontFamily}
        themeMode={themeMode} onThemeModeChange={setThemeMode}
      />

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} ModalProps={{ keepMounted: true }}>
        <ReaderFeedbackDrawer
          comments={comments} onAddComment={handleSaveComment} onAddReply={handleSaveReply} onDeleteComment={handleDeleteComment}
          chapterName={currentChapter.name || ''} onClose={() => setDrawerOpen(false)} canManageAllComments={canManageAllComments}
        />
      </Drawer>

      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{t('reader.comment_dialog.title')}</DialogTitle>
        <DialogContent>
          {quotedText && (
            <Box sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'primary.main', mb: 2, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>"{quotedText}"</Typography>
            </Box>
          )}
          <TextField autoFocus multiline rows={3} fullWidth placeholder={t('reader.comment_dialog.placeholder')} value={commentText} onChange={(e) => setCommentText(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCommentDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handlePostComment} variant="contained" disabled={!commentText.trim()}>{t('reader.comment_dialog.submit')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
